import React, { type ReactNode } from 'react'

import {
  Box,
  Button,
  Icon,
  IconBadge,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import type { PopoverMenuTouchableOptionProps as Option } from 'modules/core/components/PopoverMenu'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import { MemoizedSectionHeaderAddon } from 'modules/questionnaires/components/MemoizedSectionHeaderAddon'
import { Translations } from 'modules/questionnaires/lib/types'

import './index.styles.css'
type Props = {
  id: string
  title: string
  formType?: string
  placeholder?: string
  editing?: boolean
  collapsed?: boolean
  disabled?: boolean
  locked?: boolean
  hideLockedIcon?: boolean
  renderHeaderAddon?: (sectionId: string) => ReactNode | undefined
  options?: Option[]
  onSave: (title: string) => void
  onCollapse?: () => void
  translations: Translations['section']
}

const SectionHeader: React.FC<Props> = ({
  id,
  title,
  formType,
  placeholder,
  editing = false,
  collapsed = false,
  disabled = false,
  locked = false,
  hideLockedIcon = false,
  renderHeaderAddon,
  options,
  onCollapse,
  onSave,
  translations,
}) => {
  const [sectionTitle, setSectionTitle] = React.useState(title)
  const [lockedTooltip, toggleLockedTooltip] = useToggle(false)
  const tooltipDescription =
    formType == 'satisfaction'
      ? i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.section.locked_question_satisfaction'
        )
      : formType == 'effectiveness'
        ? i18n.t(
            'performance_process.reviews.questionnaires_by_strategy.section.locked_question_effectiveness'
          )
        : i18n.t('performance_process.reviews.questionnaires_by_strategy.section.locked_question')

  return (
    <Box
      paddingLeft="s6"
      flexDirection="row"
      justifyContent="spaceBetween"
      className={locked ? undefined : 'SectionHeader_header'}
      alignItems="center"
      gap="s8"
    >
      {!locked && (
        <Box className={'SectionHeader_dragIcon'}>
          <Icon.Medium icon="drag-and-drop" color="grey800" />
        </Box>
      )}
      {!editing && (
        <Box
          className={'SectionHeader_headerTitle'}
          flexDirection="row"
          paddingLeft={locked ? 's32' : 's0'}
          justifyContent="spaceBetween"
          alignItems="center"
          gap="s8"
          id={`section-${id}-display-title`}
        >
          <Text.Header2 weight="semibold" color="grey1000">
            {sectionTitle}
          </Text.Header2>
        </Box>
      )}
      {editing && (
        // eslint-disable-next-line factorial/no-raw-html
        <form
          className={'SectionHeader_headerForm'}
          onSubmit={(e) => {
            // Prevent rogue form submission
            e.preventDefault()
            onSave(sectionTitle)
          }}
          aria-label={placeholder}
        >
          <TextField
            focused
            value={sectionTitle}
            onChange={setSectionTitle}
            mandatory
            placeholder={placeholder}
            readonly={disabled}
            inputId={`section-${id}-input-title`}
          />
          <Button
            submit
            iconName="check"
            type="positive"
            aria-label="save"
            disabled={!sectionTitle || disabled}
          />
        </form>
      )}
      <Box
        flexDirection="row"
        alignItems="center"
        gap="s8"
        justifyContent="flexEnd"
        style={{ flexGrow: '1' }}
      >
        {renderHeaderAddon && (
          <MemoizedSectionHeaderAddon render={renderHeaderAddon} sectionId={id} />
        )}
        {!locked && options?.length && (
          <Box>
            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <Button.Decorative type="secondary" iconName="ellipsis" />
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="end">
                {options.map((option, index) => (
                  <PopoverMenu.TouchableOption key={index} {...option} />
                ))}
              </PopoverMenu.Content>
            </PopoverMenu>
          </Box>
        )}

        {locked && !hideLockedIcon && (
          <Box>
            <Tooltip.Alt
              attachment="bottom center"
              label={tooltipDescription}
              open={lockedTooltip}
              toggle={toggleLockedTooltip}
            >
              <IconBadge icon="lock-locked" color="grey400" iconColor="grey800" size="s24" />
            </Tooltip.Alt>
          </Box>
        )}

        {onCollapse && (
          <Box>
            <Button
              onClick={() => onCollapse()}
              type="secondary"
              title={translations.toggle}
              iconName={collapsed ? 'chevron-down' : 'chevron-up'}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}
export default SectionHeader
